import type { Operation } from '@apollo/client';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import type { Auth } from 'firebase/auth';
import { print } from 'graphql';
import type { GraphQLFormattedError } from 'graphql/error';
import { fetchAuthToken } from '../../firebaseIntegration/utils';
import { extractOperationName } from './extractOperationName';
import { runLocalHandlers } from './runLocalHandlers';
import type { HandlersConfigMap } from './types';

type GraphQLResponse<TData> = {
  data?: TData;
  errors?: GraphQLFormattedError[];
};

type CreateGraphQLRequestArgs = {
  auth: Auth;
  handlersConfigMap: HandlersConfigMap;
  printError: (message: string) => void;
};

export class GraphQLRequestError extends Error {
  graphQLErrors: GraphQLFormattedError[];

  constructor(graphQLErrors: GraphQLFormattedError[]) {
    super(graphQLErrors.map(({ message }) => message).join(', '));
    this.name = 'GraphQLRequestError';
    this.graphQLErrors = graphQLErrors;
  }
}

export const createGraphQLRequest =
  ({ auth, handlersConfigMap, printError }: CreateGraphQLRequestArgs) =>
  async <TData, TVariables>(document: TypedDocumentNode<TData, TVariables>, variables?: TVariables): Promise<TData> => {
    const token = await fetchAuthToken({ auth });
    const operationName = extractOperationName(document);

    const response = await fetch(`${window.location.origin}/api/graphql`, {
      body: JSON.stringify({
        operationName,
        query: print(document),
        variables: variables ?? {},
      }),
      headers: {
        authorization: token ? `Bearer ${token}` : '',
        'content-type': 'application/json',
      },
      method: 'POST',
    });

    if (!response.ok) {
      throw new Error(`[Network error]: ${response.status} ${response.statusText}`);
    }

    const result = (await response.json()) as GraphQLResponse<TData>;

    if (result.errors?.length) {
      const handledLocally = runLocalHandlers({
        graphQLErrors: result.errors as never,
        handlersConfigMap,
        operation: { query: document } as Operation,
      }).some(Boolean);

      if (!handledLocally) {
        result.errors.forEach(({ message, path }) => {
          printError(`[GraphQL error]: Message: ${message}${path ? `, Path: ${path}` : ''}`);
        });
      }

      throw new GraphQLRequestError(result.errors);
    }

    if (!result.data) {
      throw new Error('GraphQL response did not contain data');
    }

    return result.data;
  };
